import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { ProfileCard } from '../components/ProfileCard';
import { ActionButtons } from '../components/ActionButtons';
import { MatchModal } from '../components/MatchModal';
import { useSwipe } from '../hooks/useSwipe';
import { useConnectionModal } from '../hooks/useConnectionModal';
import { useConversations } from '../hooks/useConversations';
import { mockConversations, mockMessages } from '../data/mockChats';
import { User } from '../types';
import { APP_CONFIG, ROUTES } from '../constants/app';
import { ConnectionService } from '../services/connectionService';
import { SupabaseService } from '../services/supabaseService';

interface SwipePageProps {
  currentUser: User;
}

/**
 * VISTA: Página principal de swipe
 * 
 * Funcionalidades principales:
 * - Mostrar tarjetas de usuarios para hacer swipe
 * - Gestionar acciones de like/reject/super like
 * - Mostrar modal de conexión cuando hay match
 * - Navegación a conversaciones cuando se inicia chat
 */
export const SwipePage: React.FC<SwipePageProps> = ({ currentUser }) => {
  const navigate = useNavigate();
  
  // Estado para usuarios cargados desde Supabase
  const [supabaseUsers, setSupabaseUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [loadError, setLoadError] = useState<string>('');

  // Hooks para gestión de estado
  const connectionModal = useConnectionModal();
  const conversationManager = useConversations(mockConversations, mockMessages);
  
  // Hook para gestionar el swipe de tarjetas de usuarios
  const {
    currentUser: swipeUser,
    nextUser,
    connections,
    swipeLeft,
    swipeRight,
    superLike,
    rewind,
    hasMore,
    canRewind
  } = useSwipe(supabaseUsers);

  /**
   * Cargar usuarios desde Supabase al montar el componente
   */
  useEffect(() => {
    const loadUsersFromSupabase = async () => {
      console.log('🔄 Cargando usuarios desde Supabase...');
      setIsLoadingUsers(true);
      setLoadError('');

      try {
        // Obtener usuarios excluyendo el usuario actual
        const result = await SupabaseService.getUsers(currentUser.id, undefined, 20);
        
        if (result.success && result.users) {
          console.log(`✅ ${result.users.length} usuarios cargados desde Supabase para swipe`);
          console.log('🔍 Usuario actual excluido:', { 
            id: currentUser.id.substring(0, 20) + '...', 
            name: currentUser.name 
          });
          console.log('👥 Usuarios disponibles:', result.users.map(u => ({
            id: u.id.substring(0, 20) + '...',
            name: u.name,
            type: u.userType
          })));
          
          setSupabaseUsers(result.users);
          
          if (result.users.length === 0) {
            setLoadError('No hay otros usuarios disponibles para hacer swipe. ¡Sé el primero en conectar!');
          }
        } else {
          console.error('❌ Error al cargar usuarios:', result.error);
          setLoadError(result.error || 'Error al cargar usuarios');
        }
      } catch (error) {
        console.error('❌ Error inesperado al cargar usuarios:', error);
        setLoadError('Error de conexión al cargar usuarios');
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadUsersFromSupabase();
  }, [currentUser.id]);

  /**
   * Maneja el inicio de chat desde el modal de conexión
   * Crea o obtiene la conversación y navega al chat
   */
  const handleStartChat = useCallback((conversationId: string) => {
    console.log('🚀 Navegando al chat:', conversationId);
    navigate(`/chat/${conversationId}`);
    connectionModal.hideConnection();
  }, [navigate, connectionModal]);

  /**
   * Maneja las acciones de swipe en las tarjetas
   */
  const handleSwipe = (direction: 'left' | 'right') => {
    if (direction === 'left') {
      swipeLeft();
    } else {
      const hasConnection = swipeRight();
      if (hasConnection && swipeUser) {
        connectionModal.showConnection(swipeUser);
      }
    }
  };

  /**
   * Maneja las acciones de los botones de acción
   */
  const handleReject = () => {
    swipeLeft();
  };

  const handleLike = () => {
    const hasConnection = swipeRight();
    if (hasConnection && swipeUser) {
      connectionModal.showConnection(swipeUser);
    }
  };

  const handleSuperLike = () => {
    const hasConnection = superLike();
    if (hasConnection && swipeUser) {
      connectionModal.showConnection(swipeUser);
    }
  };

  /**
   * Maneja la programación de reuniones desde el modal de conexión
   */
  const handleScheduleMeeting = () => {
    // En una app real, esto abriría un programador de reuniones
    console.log('Schedule meeting with', connectionModal.connectedUser?.name);
    connectionModal.hideConnection();
  };

  // VISTA: Loading state mientras se cargan usuarios
  if (isLoadingUsers) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Cargando usuarios...</h2>
          <p className="text-gray-600">Obteniendo perfiles desde Supabase</p>
        </div>
      </div>
    );
  }

  // VISTA: Error al cargar usuarios
  if (loadError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Error al cargar usuarios</h2>
          <p className="text-gray-600 mb-6">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // VISTA: Pantalla de "no hay más usuarios"
  if (!hasMore) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">¡Has explorado todas las oportunidades!</h2>
          <p className="text-gray-600 mb-6">Vuelve más tarde para ver nuevos emprendedores e inversores.</p>
          <div className="text-sm text-gray-500">
            <p>Conexiones realizadas: {connections.length}</p>
            <p>Usuarios totales revisados: {supabaseUsers.length}</p>
          </div>
          <button
            onClick={() => navigate(ROUTES.CONVERSATIONS)}
            className="mt-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform"
          >
            Ver Conversaciones
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white relative overflow-hidden flex flex-col">
      {/* Header */}
      <Header 
        location={APP_CONFIG.DEFAULT_LOCATION}
        userType={currentUser.userType}
        currentUser={currentUser}
      />

      {/* Cards Container */}
      <main className="relative flex-1 mx-4 overflow-hidden">
        {/* Next card (background) */}
        {nextUser && (
          <ProfileCard
            key={`next-${nextUser.id}`}
            user={nextUser}
            onSwipe={() => {}}
            isTop={false}
          />
        )}
        
        {/* Current card (top) */}
        {swipeUser && (
          <ProfileCard
            key={`current-${swipeUser.id}`}
            user={swipeUser}
            onSwipe={handleSwipe}
            isTop={true}
          />
        )}
      </main>

      {/* Action Buttons */}
      <ActionButtons
        onReject={handleReject}
        onSuperLike={handleSuperLike}
        onLike={handleLike}
        onRewind={canRewind ? rewind : undefined}
      />

      {/* Connection Modal */}
      {connectionModal.isVisible && connectionModal.connectedUser && (
        <MatchModal
          isOpen={connectionModal.isVisible}
          currentUser={currentUser}
          matchedUser={connectionModal.connectedUser}
          onClose={connectionModal.hideConnection}
          onSendMessage={handleScheduleMeeting}
          onKeepSwiping={connectionModal.hideConnection}
          onStartChat={handleStartChat}
        />
      )}
    </div>
  );
};